import { ArrowLeft, ExternalLink, FileText, Link2 } from 'lucide-react'
import { useState } from 'react'

import { RelativeTime } from '@/shared/components/common/RelativeTime'
import { EmptyState, ErrorState, SkeletonRows } from '@/shared/components/data'
import { Button, Markdown } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'

import { usePortalDocument } from '../api'
import { docTypeKey, type PortalDocumentListItem } from '../types'

export interface PortalDocumentsProps {
  documents: readonly PortalDocumentListItem[]
}

/**
 * El nombre del tipo de documento, en el idioma de quien mira.
 *
 * Un tipo que el catálogo no conoce sale **crudo**: es más honesto que dibujar
 * la clave o inventarle un nombre.
 */
function useDocTypeLabel(): (docType: string) => string {
  const t = useT()
  return (docType: string) => {
    const key = docTypeKey(docType)
    return key ? t(key) : docType
  }
}

/**
 * Los documentos publicados al portal.
 *
 * Solo llegan los que alguien marcó `visibility='client'`: todo nace interno y
 * publicarlo es un acto deliberado del equipo.
 *
 * El documento se abre **sin cambiar de ruta**: el portal tiene tres URLs y
 * nada más. Un markdown se lee acá mismo (siempre por `rehype-sanitize`, que
 * es justamente lo que hace seguro mostrarle a un cliente texto que escribió
 * otra persona); un link se abre afuera, que es donde vive.
 *
 * El título y el contenido los escribió el equipo: viajan tal cual. Lo único
 * que sale del catálogo es el **tipo**, que es un enum del sistema.
 */
export function PortalDocuments({ documents }: PortalDocumentsProps) {
  const t = useT()
  const [openId, setOpenId] = useState<string | null>(null)

  if (openId) return <DocumentReader documentId={openId} onBack={() => setOpenId(null)} />

  if (documents.length === 0) {
    return (
      <EmptyState
        title={t('portal.documents.emptyTitle')}
        description={t('portal.documents.emptyDescription')}
      />
    )
  }

  return (
    <ul
      aria-label={t('portal.section.documents')}
      className="hairline flex flex-col overflow-hidden rounded-[var(--radius-card)] bg-surface-1"
    >
      {documents.map((document) => (
        <li key={document.id} className="hairline-b last:border-b-0">
          {document.kind === 'link' ? (
            <a
              href={document.external_url ?? '#'}
              target="_blank"
              rel="noreferrer"
              className="flex w-full flex-wrap items-center gap-3 px-3 py-3 text-left hover:bg-surface-2"
            >
              <Link2 size={14} aria-hidden="true" className="shrink-0 text-text-3" />
              <Meta document={document} />
              <ExternalLink size={13} aria-hidden="true" className="shrink-0 text-text-3" />
            </a>
          ) : (
            <button
              type="button"
              onClick={() => setOpenId(document.id)}
              className="flex w-full flex-wrap items-center gap-3 px-3 py-3 text-left hover:bg-surface-2"
            >
              <FileText size={14} aria-hidden="true" className="shrink-0 text-text-3" />
              <Meta document={document} />
            </button>
          )}
        </li>
      ))}
    </ul>
  )
}

function Meta({ document }: { document: PortalDocumentListItem }) {
  const docTypeLabel = useDocTypeLabel()

  return (
    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
      <span className="truncate text-sm text-text-1">{document.title}</span>
      <span className="flex items-center gap-2 text-2xs text-text-3">
        {docTypeLabel(document.doc_type)}
        <span aria-hidden="true">·</span>
        <RelativeTime value={document.updated_at} />
      </span>
    </span>
  )
}

function DocumentReader({ documentId, onBack }: { documentId: string; onBack: () => void }) {
  const t = useT()
  const docTypeLabel = useDocTypeLabel()
  const query = usePortalDocument(documentId)

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Button onClick={onBack}>
          <ArrowLeft size={13} aria-hidden="true" />
          {t('portal.documents.back')}
        </Button>
      </div>

      {query.isPending ? (
        <SkeletonRows rows={8} withSpine={false} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <article className="hairline flex flex-col gap-2 rounded-[var(--radius-card)] bg-surface-1 px-5 py-4">
          <header className="flex flex-col gap-1">
            <h3 className="font-display text-md text-text-1">{query.data?.title}</h3>
            <p className="flex items-center gap-2 text-2xs text-text-3">
              {docTypeLabel(query.data?.doc_type ?? 'other')}
              <span aria-hidden="true">·</span>
              <RelativeTime value={query.data?.updated_at ?? null} />
            </p>
          </header>

          <Markdown empty={t('portal.documents.noContent')}>
            {query.data?.content_md ?? ''}
          </Markdown>
        </article>
      )}
    </div>
  )
}
